import { useState, useCallback } from 'react';
import { postWebSearch, OutputType, SearchResult } from '../endpoints/ai/websearch_POST.schema';

interface UseWebSearchState {
  isSearching: boolean;
  results: SearchResult[];
  query: string | null;
  error: string | null;
}

export function useWebSearch() {
  const [state, setState] = useState<UseWebSearchState>({
    isSearching: false,
    results: [],
    query: null,
    error: null,
  });

  const search = useCallback(
    async (
      query: string,
      options?: { type?: 'web' | 'code' | 'docs'; num?: number },
    ): Promise<OutputType | null> => {
      if (!query.trim()) return null;

      setState((prev) => ({
        ...prev,
        isSearching: true,
        query,
        error: null,
      }));

      try {
        const response = await postWebSearch({
          query,
          type: options?.type || 'web',
          num: options?.num || 5,
        });

        setState((prev) => ({
          ...prev,
          isSearching: false,
          results: response.results,
        }));

        return response;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Search failed';
        setState((prev) => ({
          ...prev,
          isSearching: false,
          error: errorMessage,
        }));
        return null;
      }
    },
    [],
  );

  const clearResults = useCallback(() => {
    setState({
      isSearching: false,
      results: [],
      query: null,
      error: null,
    });
  }, []);

  return {
    ...state,
    search,
    clearResults,
  };
}
